import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Switch, Chip, IconButton, LinearProgress, Checkbox, Button, Toolbar, Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_REPORTS = [
	{
		name: 'Madrid → Barcelona Weekly',
		searches: 216,
		sites: ['rentalspot.demo', 'pricefly.demo'],
		hours: ['08:00', '14:00'],
		frequency: ['Mondays', 'Wednesdays', 'Fridays'],
		pickup: 'Madrid Airport (MAD)',
		dropoff: 'Barcelona Airport (BCN)',
		date: '10 Feb 2025 08:22',
		status: 'ready',
		enabled: true,
	},
	{
		name: 'London LHR → Manchester MAN',
		searches: 1080,
		sites: ['bookride.demo', 'findwheels.demo', 'autolink.demo'],
		hours: ['06:00', '12:00', '18:00'],
		frequency: ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays'],
		pickup: 'Heathrow Airport (LHR)',
		dropoff: 'Manchester Airport (MAN)',
		date: null,
		status: 'running',
		progress: 43,
		completeTasks: '464',
		totalTasks: '1080',
		enabled: true,
	},
	{
		name: 'Paris CDG → Nice NCE',
		searches: 144,
		sites: ['rentalspot.demo'],
		hours: ['10:00'],
		frequency: ['Tuesdays', 'Thursdays'],
		pickup: 'Paris CDG Airport (CDG)',
		dropoff: 'Nice Airport (NCE)',
		date: '08 Feb 2025 10:15',
		status: 'ready',
		enabled: true,
	},
	{
		name: 'Rome FCO → Milan MXP',
		searches: 72,
		sites: ['pricefly.demo', 'bookride.demo'],
		hours: ['09:00'],
		frequency: ['Mondays'],
		pickup: 'Rome Fiumicino (FCO)',
		dropoff: 'Milan Malpensa (MXP)',
		date: '03 Feb 2025 09:05',
		status: 'stopped',
		enabled: false,
	},
	{
		name: 'Munich MUC → Frankfurt FRA',
		searches: 432,
		sites: ['rentalspot.demo', 'pricefly.demo', 'findwheels.demo'],
		hours: ['07:00', '13:00', '19:00'],
		frequency: ['Mondays', 'Wednesdays', 'Fridays', 'Sundays'],
		pickup: 'Munich Airport (MUC)',
		dropoff: 'Frankfurt Airport (FRA)',
		date: '10 Feb 2025 19:08',
		status: 'ready',
		enabled: true,
	},
]

const ActionButton = ({ icon, tooltip, tourId }) => (
	<IconButton size="small" title={tooltip} sx={{ p: 0.5 }} {...(tourId && { 'data-tour-id': tourId })}>
		<IconSeeker icon={icon} size="xs" />
	</IconButton>
)

const DownloadIcon = ({ icon, color, tourId }) => (
	<IconButton size="small" sx={{ p: 0.3 }} {...(tourId && { 'data-tour-id': tourId })}>
		<IconSeeker icon={icon} size="xs" style={{ color }} />
	</IconButton>
)


export default function ExtraReportsDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader
					title={
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="subtitle1" fontWeight={600}>Reports Extra List</Typography>
							<Button
								size="small"
								variant="contained"
								startIcon={<IconSeeker icon="file-plus" size="xs" />}
								disabled
								sx={{ textTransform: 'none' }}
							>
								Create Report
							</Button>
						</Box>
					}
				/>

				<Toolbar variant="dense" sx={{ px: 2, minHeight: 40 }}>
					<Typography variant="caption" color="text.secondary">
						{DEMO_REPORTS.length} reports
					</Typography>
				</Toolbar>

				<Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' } }}>
								<TableCell padding="checkbox"><Checkbox size="small" disabled /></TableCell>
								<TableCell>Report name</TableCell>
								<TableCell align="center">Search numbers</TableCell>
								<TableCell>Sites</TableCell>
								<TableCell>Frequency Hours</TableCell>
								<TableCell>Frequency</TableCell>
								<TableCell>Pick up & Drop off</TableCell>
								<TableCell align="center">Date</TableCell>
								<TableCell align="center">Off/On</TableCell>
								<TableCell align="center">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_REPORTS.map(r => (
								<TableRow key={r.name} sx={{ '& td': { py: 1.5, fontSize: '0.8rem' } }}>
									<TableCell padding="checkbox"><Checkbox size="small" disabled /></TableCell>

									<TableCell sx={{ maxWidth: 200 }}>
										<Typography variant="body2" fontWeight={500} noWrap>{r.name}</Typography>
									</TableCell>

									<TableCell align="center">
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
											<Typography variant="body2">{r.searches.toLocaleString()}</Typography>
											{r.searches >= 1000 && <IconSeeker icon="fire" size="xs" style={{ color: '#ef5350' }} />}
										</Box>
									</TableCell>

									<TableCell>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
											{r.sites.map(s => (
												<Chip key={s} label={s.replace('.com', '')} size="small"
													sx={{ height: 20, fontSize: '0.65rem' }} />
											))}
										</Box>
									</TableCell>

									<TableCell>
										<Typography variant="caption">{r.hours.join(', ')}</Typography>
									</TableCell>

									<TableCell>
										<Typography variant="caption">
											{r.frequency.map(f => f.slice(0, 3)).join(', ')}
										</Typography>
									</TableCell>

									<TableCell>
										<Typography variant="caption" sx={{ display: 'block' }}>{r.pickup}</Typography>
										<Typography variant="caption" color="primary.main" fontWeight={500}>→ {r.dropoff}</Typography>
									</TableCell>

									<TableCell align="center" sx={{ minWidth: 120 }}>
										{r.status === 'ready' && <Box>
											<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{r.date}</Typography>
											<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>
												<DownloadIcon icon="cloud-arrow-down" color="#2e7d32" tourId="tour-date-btn-report" />
												<DownloadIcon icon="file-arrow-down" color="#1976d2" tourId="tour-date-btn-export" />
											</Box>
										</Box>}
										{r.status === 'running' && <Box>
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
												<Typography variant="caption" color="primary" fontWeight={500}>Running</Typography>
												<Typography variant="caption" color="text.secondary">
													{r.completeTasks}/{r.totalTasks}
												</Typography>
											</Box>
											<LinearProgress variant="determinate" value={r.progress} sx={{ height: 6, borderRadius: 1 }} />
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.3, display: 'block' }}>
												Click progress bar to view details
											</Typography>
										</Box>}
										{r.status === 'stopped' && <Box>
											<Typography variant="caption" color="text.secondary">{r.date}</Typography>
											<Typography variant="caption" color="error" sx={{ display: 'block' }}>Stopped</Typography>
										</Box>}
									</TableCell>

									<TableCell align="center">
										<Switch size="small" checked={r.enabled} disabled />
									</TableCell>

									<TableCell align="center">
										<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.2 }}>
											<ActionButton icon="gears" tooltip="Perform" />
											<ActionButton icon="pen" tooltip="Edit" />
											<ActionButton icon="clone" tooltip="Clone & Edit" />
											<ActionButton icon="circle-info" tooltip="Show Report Info" tourId="tour-show-report-info" />
											<ActionButton icon="trash-can" tooltip="Delete" />
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>
	</Grid>
}
